<template>
  <div class="d-flex flex-column mb-0" :class="cardClasses">
    <!-- Header (desktop only) -->
    <div v-if="isDesktop" class="d-flex align-items-center justify-content-between px24 pt24 mb8 lh28">
      <div class="d-flex align-items-center mw-0">
        <Icon name="wallet" />
        <h4 class="text-black neue fsp20 m-0 ml12 fw-medium text-nowrap text-truncate">
          {{ $t('views.Home.30') }}
        </h4>
      </div>
      <Popper :hover="true">
        <Icon name="info" />
        <template #content>
          <div class="text-white fsp14">{{ $t('views.Home.42') }}</div>
        </template>
      </Popper>
    </div>

    <!-- State 1: Empty — no wallets -->
    <div v-if="cardState === 'empty'" class="flex-grow-1 d-flex flex-column align-items-center justify-content-center" :class="contentPadding">
      <div class="d-flex flex-column align-items-center">
        <div class="icon-background">
          <Icon name="wallet" size="c24" :active="false" />
        </div>
        <h5 class="fsp20 lh28 color-menu neue mb8">{{ $t('views.Home.61') }}</h5>
        <span class="neue-roman fsp14 lh20 primary-grey text-center">{{ $t('views.Home.62') }}</span>
        <Button type="btn-warning" class="cell-btn mt24 h48 px24" @click="$emit('createKeyt')">
          <Icon name="plus" size="c20" class="mr8" />
          <span class="neue fw-medium fsp14 text-nowrap">{{ $t('views.Home.36') }}</span>
        </Button>
      </div>
    </div>

    <!-- States 2-5: Has wallets -->
    <div v-else class="flex-grow-1 d-flex flex-column gap24" :class="contentPadding">
      <!-- Balance row with currency selector chip -->
      <div class="portfolio-balance-row d-flex align-items-center justify-content-center position-relative">
        <!-- Balance -->
        <Popper :hover="true" :disabled="!overallBalance || !balanceTruncated" :startleft="true" :offset="50">
          <div class="d-flex align-items-baseline">
            <div class="d-flex align-items-baseline">
              <span v-if="overallBalance" ref="sumWrapper" v-html="sumMask(String(overallBalance))"></span>
              <span v-else class="neue fw-medium" :class="isMobile ? 'fsp20' : 'fsp28'">0<span class="fsp16 primary-grey">.00</span></span>
            </div>
          </div>
          <template #content>
            <div class="fsp14 text-white neue-roman">
              {{ $lib.formatNumberWithSeparators(overallBalance) }} {{ selectedCurr ? selectedCurr.code : '' }}
            </div>
          </template>
        </Popper>
        <!-- Currency selector chip -->
        <div v-if="selectedCurr" class="portfolio-curr-wrapper">
          <div class="portfolio-curr-chip" :class="{ 'portfolio-curr-chip--open': chipOpen }" @click="chipOpen = !chipOpen">
            <span class="portfolio-curr-chip-icon">
              <img v-if="selectedCurr.icon" :src="selectedCurr.icon" />
              <Icon v-else name="default-curr" size="c16" />
            </span>
            <span class="neue fw-medium fsp14 text-black">{{ selectedCurr.code }}</span>
            <Icon :name="chipOpen ? 'chevron-up' : 'chevron-down'" size="c16" />
          </div>
          <!-- Dropdown -->
          <div v-if="chipOpen && currKeywords.length > 1" class="portfolio-curr-dropdown">
            <div
              v-for="c in currKeywords"
              :key="c.code"
              class="portfolio-curr-dropdown-item d-flex align-items-center gap8"
              @click="selectCurrency(c)"
            >
              <span class="portfolio-curr-chip-icon">
                <img v-if="c.icon" :src="c.icon" />
                <Icon v-else name="default-curr" size="c16" />
              </span>
              <span class="neue fw-medium fsp14" :class="selectedCurr && c.code === selectedCurr.code ? 'text-black' : 'primary-grey'">{{ c.code }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Currency list -->
      <div class="portfolio-list d-flex flex-column gap8">
        <!-- States 4-5: currencies with balances and progress bars -->
        <template v-if="cardState === 'multi' || cardState === 'single'">
          <div
            v-for="item in displayCurrencies"
            :key="item.code"
            class="portfolio-list-row"
          >
            <div class="portfolio-list-bar" :class="{ 'portfolio-list-bar--full': item.percent >= 100 }" :style="{ width: item.percent + '%' }"></div>
            <div class="portfolio-list-info d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center gap8">
                <span class="portfolio-list-icon">
                  <img v-if="item.icon" :src="item.icon" />
                  <Icon v-else name="default-curr" size="c24" />
                </span>
                <span class="neue fw-medium fsp14 text-black">{{ item.code }}</span>
              </div>
              <div class="d-flex align-items-center gap8">
                <span class="neue fw-medium fsp14 text-black" v-html="formatCurrBalanceSplit(item)"></span>
                <span class="neue fw-medium fsp14 primary-grey">{{ selectedCurr ? selectedCurr.code : '' }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- State 2: zero balances — only selected currency + "N more" -->
        <template v-if="cardState === 'zero'">
          <div v-if="selectedCurr" class="portfolio-list-row">
            <div class="portfolio-list-info d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center gap8">
                <span class="portfolio-list-icon">
                  <img v-if="selectedCurr.icon" :src="selectedCurr.icon" />
                  <Icon v-else name="default-curr" size="c24" />
                </span>
                <span class="neue fw-medium fsp14 text-black">{{ selectedCurr.code }}</span>
              </div>
              <div class="d-flex align-items-center gap8">
                <span class="neue fw-medium fsp14"><span class="text-black">0</span><span class="fsp10 primary-grey">.00</span></span>
                <span class="neue fw-medium fsp14 primary-grey">{{ selectedCurr.code }}</span>
              </div>
            </div>
          </div>
          <div v-if="zeroHiddenCount > 0" class="portfolio-list-row">
            <div class="portfolio-list-info d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center gap8">
                <span class="neue fw-medium fsp14 primary-grey">{{ $t('views.Home.65') }} {{ zeroHiddenCount }} {{ $lib.pluralize(zeroHiddenCount, [$t('views.Home.66'), $t('views.Home.67'), $t('views.Home.68')]) }}</span>
              </div>
              <div class="d-flex align-items-center gap8">
                <span class="neue fw-medium fsp14"><span class="text-black">0</span><span class="fsp10 primary-grey">.00</span></span>
                <span class="neue fw-medium fsp14 primary-grey">{{ selectedCurr ? selectedCurr.code : '' }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- "N more currencies" row (states multi/single) -->
        <div v-if="cardState !== 'zero' && totalHiddenCount > 0" class="portfolio-list-row">
          <div class="portfolio-list-info d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap8">
              <span class="neue fw-medium fsp14 primary-grey">{{ $t('views.Home.65') }} {{ totalHiddenCount }} {{ $lib.pluralize(totalHiddenCount, [$t('views.Home.66'), $t('views.Home.67'), $t('views.Home.68')]) }}</span>
            </div>
            <div class="d-flex align-items-center gap8">
              <span class="neue fw-medium fsp14 primary-grey" v-html="formatHiddenBalanceSplit()"></span>
              <span class="neue fw-medium fsp14 primary-grey">{{ selectedCurr ? selectedCurr.code : '' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="d-flex gap16 mt-auto" :class="isMobile ? 'flex-column' : 'flex-row'">
        <Button
          @click="$emit('paySelect', 'deposit')"
          type="btn-warning"
          name=""
          class="cell-btn"
          :class="isMobile ? 'h40' : 'h56 flex-grow-1'"
        >
          <Icon name="deposit" size="c18" />
          <span class="mlp-8 neue fw-medium fsp14 text-nowrap">{{ $t('views.Home.27') }}</span>
        </Button>
        <Button
          @click="$emit('paySelect', 'withdraw')"
          type="btn-outline-warning"
          @mouseover="iconHover = 'withdraw'"
          @mouseleave="iconHover = false"
          name=""
          class="cell-btn"
          :class="isMobile ? 'h40' : 'h56 flex-grow-1'"
        >
          <Icon name="withdraw" size="c18" :hovered="iconHover === 'withdraw'" :active="false" />
          <span class="mlp-8 fsp14 neue fw-medium text-nowrap">{{ $t('views.Home.26') }}</span>
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/icons/Icon.vue'
import Button from '@/components/service/Button.vue'
import Popper from '@/components/service/Popper.vue'

export default {
  name: 'PortfolioCard',
  components: { Icon, Button, Popper },
  props: {
    selectedCurr: { type: [Object, Boolean], default: false },
    selectedCurrKey: { type: [String, Boolean], default: false },
    currKeywords: { type: Array, default: () => [] },
    overallBalance: { type: [Number, Boolean], default: false },
    rate: { type: [String, Number, Boolean], default: false },
  },
  data() {
    return {
      iconHover: false,
      chipOpen: false,
      maxDisplay: 3,
    }
  },
  computed: {
    isDesktop() {
      return this.$store.state.resize.w > 768
    },
    isMobile() {
      return this.$store.state.resize.w <= 768
    },
    contentPadding() {
      return this.isDesktop ? 'p24' : 'p16'
    },
    cardClasses() {
      return {
        'content h-100': this.isDesktop,
        'height_fix': this.isMobile,
      }
    },
    currenciesWithBalance() {
      return this.currKeywords.filter(c => Number(c.balance) > 0)
    },
    cardState() {
      if (this.currKeywords.length === 0) return 'empty'
      if (this.currenciesWithBalance.length === 0) return 'zero'
      if (this.currenciesWithBalance.length === 1) return 'single'
      return 'multi'
    },
    currenciesInSelectedCurr() {
      if (!this.selectedCurr) return []
      return this.currenciesWithBalance.map(c => {
        let converted = 0
        if (c.curr_code === this.selectedCurr.curr_code) {
          converted = Number(c.balance)
        } else if (c.rate && this.rate) {
          const inBase = Number(this.$lib.ev(this, `${c.balance} * ${c.rate}`))
          converted = Number(this.$lib.precRound(this, this.$lib.ev(this, `${inBase} * ${this.rate}`), this.selectedCurr.precision))
        }
        return { ...c, converted }
      }).sort((a, b) => b.converted - a.converted)
    },
    totalConverted() {
      return this.currenciesInSelectedCurr.reduce((acc, c) => acc + c.converted, 0) || 1
    },
    displayCurrencies() {
      const items = this.currenciesInSelectedCurr.map(c => ({
        ...c,
        percent: Math.round((c.converted / this.totalConverted) * 100),
      }))
      if (items.length <= this.maxDisplay) return items
      return items.slice(0, this.maxDisplay)
    },
    hiddenCount() {
      const total = this.currenciesInSelectedCurr.length
      return total > this.maxDisplay ? total - this.maxDisplay : 0
    },
    totalHiddenCount() {
      return this.currKeywords.length - this.displayCurrencies.length
    },
    zeroHiddenCount() {
      return this.currKeywords.length > 1 ? this.currKeywords.length - 1 : 0
    },
    balanceTruncated() {
      if (!this.overallBalance) return false
      const maxIntLen = this.isMobile ? 7 : 10
      return String(Math.floor(Math.abs(this.overallBalance))).length > maxIntLen
    },
  },
  methods: {
    sumMask(sum) {
      const precision = this.selectedCurr ? this.selectedCurr.precision : 2
      const num = Number(sum)
      const fixed = num.toFixed(precision)
      const arr = fixed.split('.')
      const cls1 = this.isMobile ? 'fsp20' : 'fsp28'
      const cls2 = 'fsp16'
      const maxIntLen = this.isMobile ? 7 : 10
      const intFormatted = this.$lib.formatNumberWithSeparators(arr[0])
      if (arr[0].length > maxIntLen) {
        const short = this.$lib.formatNum(this, num, 0)
        return `<span class="${cls1} neue fw-medium first-part">${short}</span>`
      }
      if (arr[1]) {
        return `<span class="${cls1} neue fw-medium first-part">${intFormatted}</span><span class='${cls2} primary-grey neue fw-medium'>.${arr[1]}</span>`
      }
      return `<span class="${cls1} neue fw-medium">${intFormatted}</span>`
    },
    splitBalance(numStr, precision) {
      const num = Number(numStr)
      const fixed = num.toFixed(precision || (this.selectedCurr ? this.selectedCurr.precision : 2))
      const arr = fixed.split('.')
      const intPart = this.$lib.formatNumberWithSeparators(arr[0])
      if (arr[1]) {
        return `<span class="text-black">${intPart}</span><span class="fsp10 primary-grey">.${arr[1]}</span>`
      }
      return `<span class="text-black">${intPart}</span>`
    },
    formatCurrBalanceSplit(item) {
      const rounded = this.$lib.precRound(this, String(item.converted), this.selectedCurr.precision)
      return this.splitBalance(rounded)
    },
    formatHiddenBalanceSplit() {
      const hidden = this.currenciesInSelectedCurr.slice(this.maxDisplay)
      const sum = hidden.reduce((acc, c) => acc + c.converted, 0)
      const rounded = this.$lib.precRound(this, String(sum), this.selectedCurr.precision)
      return this.splitBalance(rounded)
    },
    selectCurrency(c) {
      this.chipOpen = false
      this.$emit('updateState', { value: c.code })
    },
    handleOutsideClick(e) {
      if (!this.$el.contains(e.target)) {
        this.chipOpen = false
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
}
</script>

<style lang="scss" scoped>
.portfolio-balance-row {
  position: relative;
}

.portfolio-curr-wrapper {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
}

.portfolio-curr-chip {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px 10px 12px;
  border: 1px solid $primary-stroke;
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
}

.portfolio-curr-chip-icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
  }
}

.portfolio-curr-dropdown {
  position: absolute;
  right: 0;
  left: 0;
  top: 100%;
  margin-top: 4px;
  background: #fff;
  border: 1px solid $primary-stroke;
  border-radius: 8px;
  padding: 8px;
  z-index: 10;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.05);
}

.portfolio-curr-dropdown-item {
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: $bg-light-blue;
  }
}

.portfolio-list-row {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

.portfolio-list-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: $bg-light-blue;
  border-radius: 8px 0 0 8px;
  transition: width 0.3s ease;
  z-index: 0;

  &--full {
    border-radius: 8px;
  }
}

.portfolio-list-info {
  position: relative;
  z-index: 1;
  padding: 8px;
}

.portfolio-list-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
  }
}

.curr-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
  }
}

.max-w-120 {
  flex: 1 0 120px;
}
</style>
